import { useEffect, useState } from "react";
import { Play, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, assetUrl } from "@/lib/api";
import { ytThumb, ytEmbed } from "@/lib/youtube";
import { SectionHead, MaskWords } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";

export default function MediaPresence() {
  const { lang, t } = useLang();
  const m = t.media;
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/media")
      .then((r) => setItems(r.data.slice(0, 3)))
      .catch(() => setItems([]));
  }, []);

  const g = (it, k) => it[`${k}_${lang}`] || it[`${k}_ro`] || "";

  const openItem = (it) => {
    if (it.youtube_url) setPlaying(it.id);
    else if (it.external_url && it.external_url !== "#") window.open(it.external_url, "_blank");
  };

  if (!items.length) return null;

  return (
    <section id="media" className="bg-noir text-paper px-6 md:px-12 lg:px-20 py-28 md:py-40">
      <SectionHead dark label={m.label} index="" />
      <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
        <MaskWords
          Tag="h2"
          text={m.title}
          className="col-span-12 lg:col-span-8 display uppercase text-[11vw] lg:text-[9vw] leading-[0.85] break-words"
        />
        <p className="col-span-12 lg:col-span-4 text-paper/60 leading-[1.6]">{m.intro}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {items.map((it, i) => {
          const cover = assetUrl(it.cover_image) || ytThumb(it.youtube_url);
          return (
            <Reveal key={it.id} delay={(i % 3) * 0.08}>
              {playing === it.id && ytEmbed(it.youtube_url) ? (
                <div>
                  <div className="relative overflow-hidden aspect-video bg-black" data-testid={`media-inline-${i}`}>
                    <iframe
                      key={it.id}
                      src={ytEmbed(it.youtube_url, true)}
                      title={g(it, "title")}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="pt-4">
                    <div className="font-display text-2xl leading-tight">{g(it, "title")}</div>
                    <div className="eyebrow text-paper/45 mt-1">{g(it, "outlet")}</div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => openItem(it)}
                  data-testid={`media-card-${i}`}
                  data-cursor="link"
                  className="group text-left w-full"
                >
                  <div className="relative overflow-hidden aspect-video bg-white/5">
                    {cover && (
                      <img
                        src={cover}
                        alt={g(it, "title")}
                        onError={(e) => { if (e.currentTarget.src.includes("maxresdefault")) e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "mqdefault"); }}
                        className="w-full h-full object-contain opacity-85 transition-opacity duration-[1200ms] ease-out group-hover:opacity-100"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-5 left-5 eyebrow text-paper/80">{g(it, "kind")}</div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full border border-white/50 flex items-center justify-center backdrop-blur-sm transition-all duration-500 group-hover:bg-white group-hover:text-black group-hover:border-white">
                        <Play size={20} className="ml-0.5" fill="currentColor" />
                      </span>
                    </div>
                  </div>
                  <div className="pt-4">
                    <div className="font-display text-2xl leading-tight">{g(it, "title")}</div>
                    <div className="eyebrow text-paper/45 mt-1">{g(it, "outlet")}</div>
                  </div>
                </button>
              )}
            </Reveal>
          );
        })}
      </div>

      <div className="mt-14 md:mt-20">
        {m.channelUrl && (
          <a
            href={m.channelUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="youtube-channel-link"
            data-cursor="link"
            className="group inline-flex items-center gap-3 eyebrow border-b border-paper/40 pb-2 hover:border-paper transition-colors"
          >
            {m.channelLabel || "MY YOUTUBE CHANNEL"}
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </a>
        )}
      </div>
    </section>
  );
}
